import { isEmpty, isNil } from 'lodash';
import moment from 'moment';
import { useCallback, useEffect, useMemo } from 'react';
import { Menu } from '@shared/components/Menu';
import { View } from '@shared/components/View';
import { MBTextBody1, MBTextBody1Bold } from '@shared/components/typography';
import { Batch } from '@shared/scenes/cards/entityBulkActions/BatchCreateContext';
import { grid } from '@shared/styles';
import { i18n } from '@shared/util/i18n';
import { buildCustomRRule } from './utils';

const YEAR_MONTH_NUMBERS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12] as const;

export function YearOptionsMobile({
  rruleOptions,
  setRruleOptions,
}: Pick<Batch, 'rruleOptions' | 'setRruleOptions'>) {
  // Ensure we always have a valid reference date
  const dtstartDate = useMemo(() => {
    if (rruleOptions?.dtstart) {
      return new Date(rruleOptions.dtstart);
    }
    return new Date();
  }, [rruleOptions]);

  const defaultMonth = dtstartDate.getUTCMonth() + 1; // RRule months are 1-indexed
  const defaultDay = dtstartDate.getUTCDate();

  /*
   * Selected months derived from rruleOptions – fallback to start month if none.
   */
  const selectedMonths = useMemo<number[]>(() => {
    if (!rruleOptions?.bymonth) {
      return [defaultMonth];
    }
    if (Array.isArray(rruleOptions.bymonth)) {
      return rruleOptions.bymonth as number[];
    }
    return [rruleOptions.bymonth as number];
  }, [rruleOptions, defaultMonth]);

  useEffect(() => {
    if (isEmpty(rruleOptions?.bymonth)) {
      setRruleOptions(prev =>
        buildCustomRRule(
          prev,
          {
            bymonth: selectedMonths,
            bymonthday: !isNil(prev?.bymonthday) ? prev?.bymonthday : [defaultDay],
          },
          true,
        ),
      );
    }
  }, [rruleOptions?.bymonth, selectedMonths, defaultDay, setRruleOptions]);

  /*
   * Toggle a month in the selection.
   */
  const toggleMonth = useCallback(
    (monthNum: number) => {
      const isSelected = selectedMonths.includes(monthNum);
      let newSelection: number[];
      if (isSelected) {
        // Prevent clearing all months – must have at least one selected
        if (selectedMonths.length === 1) {
          return;
        }
        newSelection = selectedMonths.filter(m => m !== monthNum);
      } else {
        newSelection = [...selectedMonths, monthNum];
      }
      // Sort for deterministic ordering (Jan=1 … Dec=12)
      newSelection.sort((a, b) => a - b);
      setRruleOptions(prev =>
        buildCustomRRule(
          prev,
          {
            bymonth: newSelection,
            bymonthday: !isNil(prev?.bymonthday) ? prev?.bymonthday : [defaultDay],
          },
          true,
        ),
      );
    },
    [selectedMonths, defaultDay, setRruleOptions],
  );

  const monthsDisplay = useMemo(() => {
    const names = selectedMonths.map(m =>
      moment()
        .month(m - 1)
        .format('MMMM'),
    );
    return names.join(', ');
  }, [selectedMonths]);

  return (
    <View>
      <MBTextBody1Bold style={{ marginBottom: grid(1) }}>
        {i18n('workflowTemplate.component.dateAndRepeat.onDotDotDot')}
      </MBTextBody1Bold>
      <View row centerVertically gap={1} paddingTop={1} paddingBottom={1} style={{ width: '100%' }}>
        <Menu closeOnSelect={false} align="center">
          <Menu.Button flex label={monthsDisplay} />
          <Menu.Content style={{ minWidth: grid(32) }}>
            <Menu.Scrollable>
              {YEAR_MONTH_NUMBERS.map(monthNum => {
                const isSelected = selectedMonths.includes(monthNum);
                const isOnlySelected = selectedMonths.length === 1 && isSelected;
                return (
                  <Menu.Item
                    key={monthNum}
                    label={moment()
                      .month(monthNum - 1)
                      .format('MMMM')}
                    onClick={() => toggleMonth(monthNum)}
                    disabled={isOnlySelected}
                    closeOnSelect={false}
                    endContent={<Menu.CheckboxMarker isSelected={isSelected} />}
                  />
                );
              })}
            </Menu.Scrollable>
          </Menu.Content>
        </Menu>
        <MBTextBody1 style={{ flexShrink: 0 }}>
          {moment.localeData().ordinal(defaultDay)}
        </MBTextBody1>
      </View>
    </View>
  );
}
